import { z } from 'zod';

/**
 * Who is asking, and what they may do.
 *
 * Deliberately provider-agnostic. WorkOS (or whatever replaces it) is one adapter that
 * produces a Viewer; nothing downstream — no guard, no route, no component — knows
 * where the identity came from.
 */

/**
 * Authorisation is on PERMISSIONS, never on role names.
 *
 * Role slugs are renameable by an org admin in most identity providers, so a check
 * against "admin" is a check against a string someone else controls. Permissions are
 * ours.
 */
export const PERMISSIONS = [
  // Partner-facing
  'dashboard:read',
  'reporting:read',
  'links:read',
  'codes:read',
  'assets:read',
  'statements:read',
  'team:read',
  'team:manage',
  'profile:manage',
  // Sparkery-internal
  'partners:read',
  'partners:manage',
  'assets:manage',
  'settlement:manage',
] as const;

export type Permission = (typeof PERMISSIONS)[number];
export const permissionSchema = z.enum(PERMISSIONS);

/**
 * The two kinds of principal.
 *
 * `sparkery` is internal staff; `partner` is a partner company's own user. The split
 * decides which navigation appears and which routes exist at all, and it is derived
 * from the identity provider's organisation, not from a flag a user could hold.
 */
export const audienceSchema = z.enum(['sparkery', 'partner']);
export type Audience = z.infer<typeof audienceSchema>;

export const partnerContextSchema = z.object({
  partnerId: z.string().min(1),
  name: z.string().min(1),
  slug: z.string().min(1),
});
export type PartnerContext = z.infer<typeof partnerContextSchema>;

export const viewerSchema = z.object({
  userId: z.string().min(1),
  name: z.string().min(1),
  email: z.string().min(1),
  audience: audienceSchema,
  permissions: z.array(permissionSchema),

  /**
   * The partner whose data this viewer sees.
   *
   * Set for a partner user. Also set for internal staff while impersonating — which is
   * the point: one field decides scope, so a route handler cannot accidentally serve
   * everyone's data by forgetting to branch on the audience.
   */
  partner: partnerContextSchema.nullable(),

  /**
   * Present only while internal staff are acting as a partner.
   *
   * Its presence makes the session read-only, and it is what the "Viewing as / Quit"
   * bar renders from.
   */
  impersonation: z
    .object({
      sessionId: z.string().min(1),
      adminUserId: z.string().min(1),
      adminName: z.string().min(1),
      expiresAt: z.string().min(1),
    })
    .nullable(),
});

export type Viewer = z.infer<typeof viewerSchema>;

export const meResponseSchema = z.object({
  viewer: viewerSchema,
  /** Which identity provider produced this viewer. Shown in dev, logged in prod. */
  provider: z.string(),
});
export type MeResponse = z.infer<typeof meResponseSchema>;

/** True when the viewer holds every listed permission. */
export function can(viewer: Viewer, ...required: Permission[]): boolean {
  return required.every((permission) => viewer.permissions.includes(permission));
}

/**
 * Whether this viewer may change anything.
 *
 * Impersonated sessions are read-only by default. Checking this once in the guard
 * beats hoping every future write handler remembers.
 */
export function isReadOnly(viewer: Viewer): boolean {
  return viewer.impersonation !== null;
}

/**
 * The permissions a partner user holds.
 *
 * `accruesCommission` is the partner's terms, not the user's role: a fixed-fee partner
 * has no commission to state, and the decision was that they see none of it in the app.
 * Withholding the PERMISSION rather than hiding the nav item is what makes that real —
 * the item disappears because the navigation is derived from permissions, and the
 * endpoint refuses them too. Hiding the link alone would leave the data one URL away.
 */
export function partnerPermissions(
  isCompanyAdmin: boolean,
  accruesCommission: boolean,
): Permission[] {
  return [
    'dashboard:read',
    'reporting:read',
    'links:read',
    'codes:read',
    'assets:read',
    ...(accruesCommission ? (['statements:read'] as const) : []),
    'team:read',
    'profile:manage',
    ...(isCompanyAdmin ? (['team:manage'] as const) : []),
  ];
}

/**
 * The permissions an impersonated session holds.
 *
 * The TARGET partner's, not the admin's. Acting as a partner means seeing what they
 * see — an admin who kept their own permissions would get the admin navigation while
 * scoped to someone else's data, which is both wrong and confusing.
 *
 * It is also the safer model: an impersonated session cannot reach the admin sections
 * at all, so there is no path from "viewing as Bodyline" to managing every partner.
 * Writes are blocked separately, by isReadOnly.
 */
export function impersonatedPermissions(accruesCommission: boolean): Permission[] {
  return partnerPermissions(true, accruesCommission);
}

/** The permissions a local impersonation target holds. */
export function impersonatedTargetPermissions(
  isCompanyAdmin: boolean,
  accruesCommission: boolean,
): Permission[] {
  return partnerPermissions(isCompanyAdmin, accruesCommission);
}

/** The permissions Sparkery staff hold. */
export function sparkeryPermissions(): Permission[] {
  return [
    'dashboard:read',
    'reporting:read',
    'assets:read',
    'assets:manage',
    'statements:read',
    'profile:manage',
    'partners:read',
    'partners:manage',
    'settlement:manage',
  ];
}
